// Minimal client-side router. Pattern-matching against window.location.pathname.
// Routes register Component factories; auth guard redirects when needed.

import { t } from './i18n';
import { errorKind, errorScreenFor } from './pages/ErrorScreen';

type RouteFactory = (params: Record<string, string>) => Component;
// A guard either allows the route, redirects elsewhere, or renders a screen in
// place (keeping the URL) — the last is how a capability denial shows
// the friendly forbidden surface instead of bouncing the user.
type GuardResult =
  | { allowed: true }
  | { allowed: false; redirectTo: string }
  | { allowed: false; render: RouteFactory };
type Guard = (path: string) => GuardResult;

export interface Component {
  mount(host: HTMLElement): void;
  unmount?(): void;
}

// lazy defers a route's code to its first visit, so a surface most users never
// open is not in everyone's first-load bundle. The admin editors are the case
// that motivated it: they are desk-only and permission-gated, yet every driver on
// a phone was downloading them — and they pushed the main chunk past the gzip
// budget on their own.
//
// It exists because RouteFactory is synchronous: this returns a Component
// immediately and swaps the real one in when the chunk lands. A navigation away
// mid-load is respected via the destroyed flag, so a slow chunk can never mount
// itself into a host the router has already moved on from.
//
// A rejected import is mounted, not swallowed (issue 279). renderRoute wipes the
// region before it mounts, so a silent failure leaves the content area
// permanently blank next to a nav that still renders — the app looks alive and
// broken, and only a manual reload recovers. The failure path mirrors the
// success path exactly: it honours destroyed, and it parks the error screen in
// inner so unmount() still cleans up (otherwise a stale error screen leaks into
// the next route).
export function lazy(load: () => Promise<Component>): Component {
  let inner: Component | null = null;
  let destroyed = false;

  return {
    mount(target: HTMLElement): void {
      void load().then(
        (component) => {
          if (destroyed) return;
          inner = component;
          inner.mount(target);
        },
        (err: unknown) => {
          if (destroyed) return;
          inner = chunkErrorScreen(err);
          inner.mount(target);
        }
      );
    },
    unmount(): void {
      destroyed = true;
      inner?.unmount?.();
      inner = null;
    },
  };
}

// chunkErrorScreen builds the surface a failed route chunk falls back to.
//
// The retry reloads the page rather than re-calling load(). That is deliberate:
// browsers cache a *rejected* dynamic import, so an in-place re-import re-rejects
// instantly and the button reads as dead. The trigger that actually produces this
// failure is a redeploy evicting the old chunk hashes (issues 292/294), and the
// only real recovery there is fresh HTML pointing at the new hashes — which is
// what a reload gets. It is the honest affordance.
//
// The strings are resolved here, at failure time, not at module eval: ErrorScreen's
// PRESETS capture t() when the module loads, before a non-en catalogue has been
// registered (issue 287), so anything this path renders has to be passed in as an
// override to come out in the user's language. Offline is left to its own preset —
// "you're offline" is the truer message than "this page didn't load", and it
// already carries the same reload action.
function chunkErrorScreen(err: unknown): Component {
  const action = { label: t('common.tryAgain'), onClick: () => window.location.reload() };
  if (errorKind(err) !== 'generic') return errorScreenFor(err, { action });
  return errorScreenFor(err, {
    title: t('error.chunk.title'),
    copy: t('error.chunk.copy'),
    action,
  });
}

type Route = {
  pattern: RegExp;
  paramNames: string[];
  factory: RouteFactory;
  isPublic: boolean;
};

const routes: Route[] = [];
let guard: Guard | null = null;
let host: HTMLElement | null = null;
let active: Component | null = null;
let notFoundFactory: RouteFactory = () => textComponent(t('common.notFound'));

export function registerRoute(path: string, factory: RouteFactory, isPublic = false): void {
  const paramNames: string[] = [];
  const re = new RegExp(
    '^' +
      path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name) => {
        paramNames.push(name);
        return '([^/]+)';
      }) +
      '/?$'
  );
  routes.push({ pattern: re, paramNames, factory, isPublic });
}

export function setNotFound(factory: RouteFactory): void {
  notFoundFactory = factory;
}

export function setAuthGuard(g: Guard): void {
  guard = g;
}

export function initRouter(mountPoint: HTMLElement): void {
  host = mountPoint;
  window.addEventListener('popstate', renderRoute);
  document.addEventListener('click', (e) => {
    // Modified clicks and non-primary buttons belong to the browser: new tab,
    // new window, download. Intercepting them broke the side-by-side desk
    // workflow (issue 286). No auxclick listener is needed — browsers dispatch
    // click only for the primary button, so the button check is a backstop.
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = (e.target as HTMLElement | null)?.closest?.('a[href^="/"]');
    if (!target) return;
    const anchor = target as HTMLAnchorElement;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('//')) return;
    if (anchor.target === '_blank') return;
    if (anchor.hasAttribute('download') || anchor.relList.contains('external')) return;
    e.preventDefault();
    navigate(href);
  });
  renderRoute();
}

/**
 * Go to a path. `replace` swaps the current history entry instead of stacking a
 * new one (issue 295).
 *
 * The distinction is who decided to go there. A user following a link should be
 * able to come back, so that pushes. A redirect the user never asked for — the
 * auth guard bouncing them to /login, or the sign-out landing — must not leave
 * an entry, because Back would return to a URL that only redirects again.
 */
export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  if (location.pathname + location.search !== path) {
    if (opts.replace) history.replaceState(null, '', path);
    else history.pushState(null, '', path);
  }
  renderRoute();
}

// True only while a refreshRoute() render is running. Read it from a component's
// mount() — synchronous inside the render — to tell "the same page rebuilt under
// you" apart from "the user navigated here". See isRouteRefresh below.
let refreshing = false;

/**
 * Re-render the current path from scratch: unmount what's there, run the route
 * factory again, mount the result (issue 297).
 *
 * This exists for the company switch. Grants are per-company, so a switch changes
 * what the page should be showing and whether the user may see it at all — but
 * only the nav was being refreshed, so every screen but two kept displaying, and
 * editing against, the previous company's data.
 *
 * Remounting rather than asking pages to reload themselves is what makes it
 * uniform: components here are one-shot (factory → mount → unmount), so a fresh
 * factory call IS the reload, and no page can forget to subscribe. It also gets
 * issue 298 for nothing — renderRoute re-runs the auth guard, so a switch into a
 * company whose role lacks this module now lands on the forbidden screen instead
 * of leaving the page mounted to collect 403s.
 *
 * In-progress form state is discarded. That is the point, not a side effect: the
 * half-typed state belonged to the company the user just left.
 */
export function refreshRoute(): void {
  refreshing = true;
  try {
    renderRoute();
  } finally {
    refreshing = false;
  }
}

/**
 * Whether the render currently in flight is a refresh rather than a navigation.
 *
 * Only meaningful synchronously during mount(). A page that behaves differently
 * on arrival than on rebuild — moving focus, say — must capture this while
 * mounting; by the time an async data load resolves, the render is long over.
 */
export function isRouteRefresh(): boolean {
  return refreshing;
}

function renderRoute(): void {
  if (!host) return;
  const mountPoint = host;
  const path = location.pathname;

  const mount = (factory: RouteFactory, params: Record<string, string>): void => {
    if (active && active.unmount) active.unmount();
    mountPoint.innerHTML = '';
    active = factory(params);
    active.mount(mountPoint);
  };

  if (guard) {
    const result = guard(path);
    if (!result.allowed) {
      if ('render' in result) {
        mount(result.render, {});
        return;
      }
      // replace, never push (issue 295). Pushing trapped the Back button: a deep
      // link to a guarded page put /login on top of it, Back returned to the
      // guarded URL, the guard fired again and pushed /login again — history grew
      // on every press and the user could not leave. Replacing means the guarded
      // URL never becomes an entry, so Back lands on whatever preceded the deep
      // link, or leaves the app. The path check below stays as the same-path
      // recursion brake.
      if (path !== result.redirectTo) {
        navigate(result.redirectTo, { replace: true });
        return;
      }
    }
  }

  const match = routes.find((r) => r.pattern.test(path));
  const params: Record<string, string> = {};
  let factory: RouteFactory = notFoundFactory;
  if (match) {
    const captured = match.pattern.exec(path);
    if (captured) {
      match.paramNames.forEach((name, i) => {
        params[name] = decodeURIComponent(captured[i + 1] ?? '');
      });
    }
    factory = match.factory;
  }
  mount(factory, params);
}

function textComponent(text: string): Component {
  return {
    mount(h) {
      const p = document.createElement('p');
      p.textContent = text;
      p.style.padding = 'var(--space-xl)';
      h.appendChild(p);
    },
  };
}
